// src/shared/components/Layout/Layout.tsx
import { useEffect } from 'react';
import { Outlet, useNavigate, Navigate } from 'react-router-dom';
import SideBar from './SideBar';
import BubbleNav from './BubbleNav';
import TopBar from './TopBar';
import { HOT_KEYS } from '../../navigation/hotkeys';
import { useIsMobile } from '@/shared/components/DevMobileEmulator/DeviceModeContext';
import { shouldForceTableOpsLanding } from '@/shared/auth/shouldForceTableOpsLanding';
import { useAuth } from '@/shared/auth/session';

type Shortcut = {
  /** single character, matched case‐insensitive */
  key: string;
  /** whether ctrl must be held */
  ctrl?: boolean;
  /** whether alt must be held */
  alt?: boolean;
  /** whether shift must be held */
  shift?: boolean;
  /** target route */
  path: string;
};

const SHORTCUTS: Shortcut[] = HOT_KEYS.map(({ key, path }) => ({
  key,
  path,
  ctrl:  true,
  shift: true,
}))

export default function Layout() {
  const navigate = useNavigate();
  const forceMobile = useIsMobile();
  const { user, capabilities, nav } = useAuth();

  // Set a default title for routes that don't override it


  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ignore if focused on an input, textarea, or select
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      for (const { key, ctrl, alt, shift, path } of SHORTCUTS) {
        if (
          e.key.toLowerCase() === key.toLowerCase() &&
          (!!ctrl === e.ctrlKey) &&
          (!!alt === e.altKey) &&
          (!!shift === e.shiftKey)
        ) {
          e.preventDefault();
          navigate(path);
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Table-ops operators never see the console shell — bounce any protected
  // route to the full-bleed kiosk SPA.
  if (shouldForceTableOpsLanding({ user, capabilities, nav })) {
    return <Navigate to="/tableops" replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Top-right user status bar */}
      <TopBar />

      {/* Desktop sidebar (>=md) — hidden when device emulator is active */}
      {!forceMobile && (
        <div className="hidden md:block">
          <SideBar />
        </div>
      )}

      {/* Mobile bubble nav — always visible in device mode, otherwise <md only */}
      <BubbleNav forceMobile={forceMobile} />

      {/*  allow the main column to shrink → sidebar keeps its w-52  */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-black text-neutral-200">
        <main className={`flex-1 min-h-0 overflow-y-auto pr-2 pb-2 pl-2 ${
          forceMobile
            ? 'pt-14'
            : 'pt-14 md:pt-1.5 md:pr-3 md:pb-3 md:pl-3'
        }`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
